// components/WatercolorRuler.tsx
import * as React from "react";
import { Fragment } from "react";
import { ThreeYearChange } from "../../api/getBTCData";
import { currencyFormatter } from "../../utils/currencyFormatter";

type RulerProps = {
  dates: string[];                    // raw ISO date strings aligned to chartData order
  threeYearData: {
    startDate: string;
    endDate: string | null;
    startClose: string | number;
    endClose: string | number | null;
    pctChange: string | number | null;
  }[];
  data: { minRow: ThreeYearChange | null; maxRow: ThreeYearChange | null } | undefined;
  startIndex: number;                 // controlled selected start index
  onChange: (idx: number) => void;    // update selected start index
  windowSteps: number;                // number of steps covering 3 years
};

export const WatercolorRuler: React.FC<RulerProps> = ({
  dates,
  threeYearData,
  data,
  startIndex,
  onChange,
  windowSteps,
}) => {
  // NEW: the index used for the readout under the ruler
  const [displayIdx, setDisplayIdx] = React.useState(startIndex);

  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const sliderRef = React.useRef<HTMLDivElement | null>(null);
  const [trackWidth, setTrackWidth] = React.useState(0);

  const geom = React.useRef({ left: 0, width: 1 });
  const dragGeom = React.useRef({ left: 0, width: 1 });
  const dragging = React.useRef(false);
  const isInteracting = React.useRef(false); // NEW
  const dragOffsetPx = React.useRef(0);
  const currentLeftPx = React.useRef(0);

  const liveRAF = React.useRef<number | null>(null);
  const pendingIdx = React.useRef<number | null>(null);
  const lastLiveIdxRef = React.useRef<number>(startIndex);

  // NEW: debounce timer to "unfreeze" after interaction
  const settleTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const SETTLE_MS = 150;

  const maxStart = Math.max(0, dates.length - 1 - windowSteps);

  const idxToPx = React.useCallback(
    (idx: number, widthOverride?: number) => {
      const w = widthOverride ?? (trackWidth || geom.current.width);
      return (w * idx) / Math.max(1, dates.length - 1);
    },
    [dates.length, trackWidth]
  );
  
  const pxToIdxClamped = React.useCallback(
    (px: number, widthOverride?: number) => {
      const w = Math.max(1, widthOverride ?? (trackWidth || geom.current.width));
      const raw = (px / w) * (dates.length - 1);
      const snapped = Math.round(raw);
      return Math.min(Math.max(0, snapped), maxStart);
    },
    [dates.length, maxStart, trackWidth]
  );

  // Auto-init first 3y span
  React.useEffect(() => {
    if (!dates.length || !windowSteps) return;
    const valid = startIndex >= 0 && startIndex <= maxStart;
    if (!valid) onChange(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates.length, windowSteps, maxStart]);

  // Measure (not while dragging)
  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      if (dragging.current) return;
      const r = track.getBoundingClientRect();
      geom.current = { left: r.left, width: r.width };
      const leftPx = idxToPx(startIndex);
      currentLeftPx.current = leftPx;
      if (sliderRef.current) sliderRef.current.style.transform = `translateX(${leftPx}px)`;
      setTrackWidth(r.width);

    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [idxToPx, startIndex, windowSteps]);

  const sliderWidthPx = React.useMemo(() => {
    const s = startIndex;
    const e = Math.min(dates.length - 1, s + windowSteps);
    return Math.max(0, idxToPx(e) - idxToPx(s));
  }, [dates.length, startIndex, windowSteps, idxToPx]);

  React.useEffect(() => {
    lastLiveIdxRef.current = startIndex;
    // If not interacting, ensure display syncs (after debounce below)
  }, [startIndex]);

  React.useEffect(() => {
    if (dates.length && windowSteps) setDisplayIdx(startIndex);
  }, [dates.length, windowSteps, startIndex]);

  React.useEffect(() => {
    if (dragging.current) return;
    const leftPx = idxToPx(startIndex);
    currentLeftPx.current = leftPx;
    if (sliderRef.current) sliderRef.current.style.transform = `translateX(${leftPx}px)`;
  }, [startIndex, idxToPx]);

  const flushLiveChange = () => {
    if (liveRAF.current) return;
    liveRAF.current = requestAnimationFrame(() => {
      liveRAF.current = null;
      if (pendingIdx.current == null) return;
      const idx = pendingIdx.current;
      pendingIdx.current = null;
      if (idx !== startIndex) onChange(idx);
    });
  };

  // NEW: debounced “unfreeze” — after user stops interacting,
  // keep the last shown dates for SETTLE_MS, then snap to startIndex.
  const scheduleSettle = React.useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      isInteracting.current = false;
      setDisplayIdx((prev) => (prev !== startIndex ? startIndex : prev));
      settleTimer.current = null;
    }, SETTLE_MS);
  }, [startIndex]);

  const onPointerDown = (e: React.PointerEvent) => {
    const slider = sliderRef.current;
    const track = trackRef.current;
    if (!slider || !track) return;
    if (settleTimer.current) { clearTimeout(settleTimer.current); settleTimer.current = null; }
    isInteracting.current = true;

    slider.setPointerCapture(e.pointerId);
    dragging.current = true;

    const r = track.getBoundingClientRect();
    dragGeom.current = { left: r.left, width: r.width };

    const sliderRect = slider.getBoundingClientRect();
    dragOffsetPx.current = e.clientX - sliderRect.left;
    slider.classList.add("dragging");
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const { left, width } = dragGeom.current;

    const rawLeft = e.clientX - left - dragOffsetPx.current;
    const minPx = 0;
    const maxPx = idxToPx(maxStart, width);
    const clamped = Math.min(Math.max(minPx, rawLeft), maxPx);

    currentLeftPx.current = clamped;
    if (sliderRef.current) sliderRef.current.style.transform = `translateX(${clamped}px)`;

    const liveIdx = pxToIdxClamped(clamped, width);
    lastLiveIdxRef.current = liveIdx;
    pendingIdx.current = liveIdx;
    setDisplayIdx(liveIdx); // show live dates immediately
    flushLiveChange();
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    const slider = sliderRef.current;
    if (slider) slider.classList.remove("dragging");

    const commitIdx = lastLiveIdxRef.current;
    if (commitIdx !== startIndex) onChange(commitIdx);

    const finalPx = idxToPx(commitIdx);
    currentLeftPx.current = finalPx;
    if (sliderRef.current) sliderRef.current.style.transform = `translateX(${finalPx}px)`;

    // Start freeze timer: keep current displayIdx for a moment, then sync
    scheduleSettle();
  };

  const onTrackClick = (e: React.MouseEvent) => {
    if (dragging.current) return;
    if (settleTimer.current) { clearTimeout(settleTimer.current); settleTimer.current = null; }
    isInteracting.current = true;

    const { left, width } = geom.current;
    const clickX = e.clientX - left - sliderWidthPx / 2;
    const idx = pxToIdxClamped(clickX, width);
    setDisplayIdx(idx);
    if (idx !== startIndex) onChange(idx);

    scheduleSettle();
  };

  const wheelRAF = React.useRef<number | null>(null);
  const onWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    isInteracting.current = true;
    if (wheelRAF.current) return;
    wheelRAF.current = requestAnimationFrame(() => {
      wheelRAF.current = null;
      const step = e.deltaX > 0 ? 2 : -2;
      const next = Math.min(Math.max(0, startIndex + step), maxStart);
      setDisplayIdx(next);
      if (next !== startIndex) onChange(next);
      scheduleSettle();
    });
  };

  // Use displayIdx for the readout (frozen briefly after interaction)
  const row = threeYearData[displayIdx];

  return (
    <div className="flex flex-col gap-2 select-none">
      <div
        ref={trackRef}
        onClick={onTrackClick}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="relative w-full h-10 rounded-xl bg-neutral-800/70 border border-neutral-700 overflow-hidden"
        style={{ boxShadow: "inset 0 0 20px rgba(255,255,255,0.05)" }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to right, rgba(255,255,255,0.05) 0 1px, transparent 1px 12px)",
          }}
        />
        <div
          ref={sliderRef}
          className="absolute top-1/2 -translate-y-1/2 h-8 rounded-lg border border-orange-400/40"
          style={{
            width: Math.max(
              0,
              idxToPx(Math.min(dates.length - 1, displayIdx + windowSteps)) -
                idxToPx(displayIdx)
            ),
            transform: `translateX(${idxToPx(startIndex)}px)`,
            background:
              "linear-gradient(180deg, rgba(249,115,22,0.25), rgba(249,115,22,0.08))",
            boxShadow:
              "0 0 24px rgba(249,115,22,0.25), inset 0 0 20px rgba(249,115,22,0.20)",
            willChange: "transform",
            transition: dragging.current ? "none" : "transform 120ms ease-out",
            touchAction: "none",
          }}
          title="Drag to move the 3-year window"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-8 w-1.5 rounded-sm bg-orange-300/70 pointer-events-none"
          style={{ transform: `translateX(${idxToPx(startIndex) - 2}px)` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-8 w-1.5 rounded-sm bg-orange-300/70 pointer-events-none"
          style={{
            transform: `translateX(${idxToPx(
              Math.min(dates.length - 1, startIndex + windowSteps)
            ) + 2}px)`,
          }}
        />
      </div>
  
      {row && row.endDate && row.pctChange !== null && (
        <>
          {/* Current selection cards */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 w-full mt-2">
            {/* Start Date */}
            <div className="flex flex-col items-center justify-center rounded-xl bg-neutral-800/70 border border-neutral-700 p-3 shadow-sm">
              <span className="text-neutral-400 text-xs uppercase tracking-wide">
                Induló dátum
              </span>
              <span className="text-neutral-100 text-lg font-semibold">
                {row.startDate}
              </span>
            </div>
  
            {/* Start Price */}
            <div className="flex flex-col items-center justify-center rounded-xl bg-neutral-800/70 border border-neutral-700 p-3 shadow-sm">
              <span className="text-neutral-400 text-xs uppercase tracking-wide">
                Induló árfolyam
              </span>
              <span className="text-neutral-100 text-lg font-semibold">
                {row.startClose != null
                  ? currencyFormatter(Number(row.startClose), "USD")
                  : "—"}
              </span>
            </div>
  
            {/* End Date */}
            <div className="flex flex-col items-center justify-center rounded-xl bg-neutral-800/70 border border-neutral-700 p-3 shadow-sm">
              <span className="text-neutral-400 text-xs uppercase tracking-wide">
                Záró dátum
              </span>
              <span className="text-neutral-100 text-lg font-semibold">
                {row.endDate}
              </span>
            </div>
  
            {/* End Price */}
            <div className="flex flex-col items-center justify-center rounded-xl bg-neutral-800/70 border border-neutral-700 p-3 shadow-sm">
              <span className="text-neutral-400 text-xs uppercase tracking-wide">
                Záró árfolyam
              </span>
              <span className="text-neutral-100 text-lg font-semibold">
                {row.endClose != null
                  ? currencyFormatter(Number(row.endClose), "USD")
                  : "—"}
              </span>
            </div>
  
            {/* Return */}
            <div className="flex flex-col items-center justify-center rounded-xl bg-neutral-800/70 border border-neutral-700 p-3 shadow-sm">
              <span className="text-neutral-400 text-xs uppercase tracking-wide">
                Hozam
              </span>
              <span
                className={`text-lg md:text-xl font-bold ${
                  Number(row.pctChange) >= 0 ? "text-green-400" : "text-red-400"
                }`}
              >
                {Number(row.pctChange).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
                %
              </span>
            </div>
          </div>
  
          {/* Best/Worst period summary cards */}
          {data && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mt-2">
              {/* Best (max) */}
              {data.maxRow && (
                <div className="flex flex-col gap-3 items-center justify-center rounded-2xl bg-neutral-900/60 border border-neutral-700 p-5 shadow-md">
                  <span className="text-neutral-400 text-xs uppercase tracking-widest">
                    Legjobb hozam időszak
                  </span>
  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-center">
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Kezdő dátum
                      </span>
                      <span className="text-neutral-100 text-base font-medium">
                        {data.maxRow.startDate ?? "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Záró dátum
                      </span>
                      <span className="text-neutral-100 text-base font-medium">
                        {data.maxRow.endDate ?? "—"}
                      </span>
                    </div>
                  </div>
  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-center">
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Kezdő árfolyam
                      </span>
                      <span className="text-neutral-100 text-base font-semibold">
                        {data.maxRow.startClose != null
                          ? currencyFormatter(Number(data.maxRow.startClose), "USD")
                          : "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Záró árfolyam
                      </span>
                      <span className="text-neutral-100 text-base font-semibold">
                        {data.maxRow.endClose != null
                          ? currencyFormatter(Number(data.maxRow.endClose), "USD")
                          : "—"}
                      </span>
                    </div>
                  </div>
  
                  <div className="flex flex-col items-center">
                    <span className="text-neutral-400 text-xs uppercase">
                      Hozam
                    </span>
                    <span
                      className={`text-2xl font-bold ${
                        Number(data.maxRow.pctChange) >= 0
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {data.maxRow.pctChange != null
                        ? Number(data.maxRow.pctChange).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })
                        : "—"}
                      %
                    </span>
                  </div>
                </div>
              )}
  
              {/* Worst (min) */}
              {data.minRow && (
                <div className="flex flex-col gap-3 items-center justify-center rounded-2xl bg-neutral-900/60 border border-neutral-700 p-5 shadow-md">
                  <span className="text-neutral-400 text-xs uppercase tracking-widest">
                    Legrosszabb hozam időszak
                  </span>
  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-center">
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Kezdő dátum
                      </span>
                      <span className="text-neutral-100 text-base font-medium">
                        {data.minRow.startDate ?? "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Záró dátum
                      </span>
                      <span className="text-neutral-100 text-base font-medium">
                        {data.minRow.endDate ?? "—"}
                      </span>
                    </div>
                  </div>
  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-center">
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Kezdő árfolyam
                      </span>
                      <span className="text-neutral-100 text-base font-semibold">
                        {data.minRow.startClose != null
                          ? currencyFormatter(Number(data.minRow.startClose), "USD")
                          : "—"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-neutral-400 text-xs uppercase">
                        Záró árfolyam
                      </span>
                      <span className="text-neutral-100 text-base font-semibold">
                        {data.minRow.endClose != null
                          ? currencyFormatter(Number(data.minRow.endClose), "USD")
                          : "—"}
                      </span>
                    </div>
                  </div>
  
                  <div className="flex flex-col items-center">
                    <span className="text-neutral-400 text-xs uppercase">
                      Hozam
                    </span>
                    <span
                      className={`text-2xl font-bold ${
                        Number(data.minRow.pctChange) >= 0
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {data.minRow.pctChange != null
                        ? Number(data.minRow.pctChange).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })
                        : "—"}
                      %
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
  
};
